import React, { useState, useEffect } from 'react';

const NLMDrugLookup = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [drugResults, setDrugResults] = useState([]);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Generic relevance scoring for any drug name
    const calculateRelevance = (drug, searchTerm) => {
        const searchLower = searchTerm.toLowerCase();
        const nameLower = drug.name.toLowerCase();
        const synonymLower = (drug.synonym || '').toLowerCase();

        // Exact match in name
        if (nameLower === searchLower) return 100;

        // Name starts with search term (e.g., "para" matches "paracetamol")
        if (nameLower.startsWith(searchLower)) return 90;

        // Search term appears as a complete word in name
        if (nameLower.split(' ').includes(searchLower)) return 80;

        // Contains search term in name
        if (nameLower.includes(searchLower)) return 70;

        // Contains search term in synonym
        if (synonymLower.includes(searchLower)) return 60;

        return 0;
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!searchTerm.trim()) {
                setDrugResults([]);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const response = await fetch(
                    `https://rxnav.nlm.nih.gov/REST/drugs.json?name=${encodeURIComponent(searchTerm.trim())}`
                );

                if (!response.ok) {
                    throw new Error(`HTTP error ${response.status}`);
                }

                const data = await response.json();
                const results = [];

                if (data.drugGroup?.conceptGroup) {
                    const validGroups = data.drugGroup.conceptGroup.filter(
                        group => Array.isArray(group.conceptProperties)
                    );

                    validGroups.forEach(group => {
                        group.conceptProperties.forEach(concept => {
                            results.push({
                                name: concept.name || 'N/A',
                                synonym: concept.synonym || 'No synonym available',
                                rxcui: concept.rxcui || 'N/A',
                                tty: group.tty || 'N/A'
                            });
                        });
                    });
                }

                // Remove duplicates based on rxcui
                const uniqueResults = [...new Map(results.map(item => [item.rxcui, item])).values()];

                // Sort results by relevance
                const sortedResults = uniqueResults
                    .map(drug => ({
                        ...drug,
                        relevance: calculateRelevance(drug, searchTerm)
                    }))
                    .filter(drug => drug.relevance > 0)
                    .sort((a, b) => b.relevance - a.relevance);

                setDrugResults(sortedResults);
                setError(sortedResults.length === 0 ? 'No drug results found' : null);
            } catch (error) {
                console.error('Error fetching drug data:', error);
                setDrugResults([]);
                setError('Error fetching drug data');
            } finally {
                setIsLoading(false);
            }
        };

        const debounceTimeout = setTimeout(() => {
            if (searchTerm) {
                fetchData();
            }
        }, 300);

        return () => clearTimeout(debounceTimeout);
    }, [searchTerm]);

    return (
        <div className="p-4 max-w-4xl mx-auto">
            <div className="mb-6">
                <input
                    type="text"
                    placeholder="Search for a drug..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>

            {isLoading && (
                <div className="text-center text-gray-600">Loading...</div>
            )}

            {error && (
                <div className="p-4 mb-4 text-red-700 bg-red-100 rounded-lg">
                    {error}
                </div>
            )}

            {!isLoading && !error && drugResults.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse bg-white shadow-sm rounded-lg">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-gray-600 border-b">Name</th>
                                <th className="px-4 py-2 text-left text-gray-600 border-b">Synonym</th>
                                <th className="px-4 py-2 text-left text-gray-600 border-b">RxCUI</th>
                                <th className="px-4 py-2 text-left text-gray-600 border-b">Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            {drugResults.map((drug, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-4 py-2 border-b">{drug.name}</td>
                                    <td className="px-4 py-2 border-b">{drug.synonym}</td>
                                    <td className="px-4 py-2 border-b">{drug.rxcui}</td>
                                    <td className="px-4 py-2 border-b">{drug.tty}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!isLoading && !error && searchTerm && drugResults.length === 0 && (
                <div className="text-center text-gray-600">
                    No results found for "{searchTerm}"
                </div>
            )}
        </div>
    );
};

export default NLMDrugLookup;